/**
 * The panel's shell: who is signed in, which screen is showing, and the frame
 * around it.
 *
 * Routing is on the URL fragment (`#/providers/abc123`). A fragment needs no
 * server configuration at all, which matters because the API lives on the same
 * origin under `/api/` and a catch-all rewrite for a real path router would sit
 * in front of it. Nothing here is linked to from outside, so there is no reason
 * to want pretty URLs.
 */
import React, { useCallback, useEffect, useState } from "react";

import { ApiError, api, whenSignedOut } from "./api.js";
import SignIn from "./screens/sign-in.jsx";
import Dashboard from "./screens/dashboard.jsx";
import ItemList from "./screens/item-list.jsx";
import ItemForm from "./screens/item-form.jsx";
import Media from "./screens/media.jsx";
import Seo from "./screens/seo.jsx";
import HomePicks from "./screens/home-picks.jsx";
import Users from "./screens/users.jsx";
import Account from "./screens/account.jsx";
import { COLLECTIONS, collectionBySlug, listKey } from "../shared/collections.js";

/**
 * `#/providers/abc` -> `{ parts: ["providers", "abc"], query: {} }`;
 * `#/faq?status=published&page=unplaced` -> the list, with those filters set.
 * The query is how a dashboard card lands on the rows it counted.
 */
function readRoute() {
  const raw = window.location.hash.replace(/^#\/?/, "");
  const [path, search = ""] = raw.split("?");
  return { parts: path.split("/").filter(Boolean), query: Object.fromEntries(new URLSearchParams(search)) };
}

function useRoute() {
  const read = readRoute;
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const onChange = () => setRoute(read());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}

export const go = (path) => {
  window.location.hash = `#/${path.replace(/^\/+/, "")}`;
};

// The collections come from the registry, so a new one is in the nav the day
// it is registered and nowhere else has to be told.
const NAV = [
  { path: "", label: "Dashboard" },
  ...COLLECTIONS.map((c) => ({ path: c.slug, label: c.many })),
  { path: "media", label: "Pictures" },
  { path: "seo", label: "Search", needs: "seo" },
  { path: "home", label: "Home page", needs: "home" },
  { path: "users", label: "Accounts", needs: "users" },
];

export default function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState(null);
  const route = useRoute();

  const refresh = useCallback(async () => {
    try {
      setSession(await api.get("auth/me"));
    } catch (error) {
      setFatal(error instanceof ApiError ? error.message : "The panel could not start.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    whenSignedOut(() => setSession((s) => (s ? { ...s, user: null } : s)));
    refresh();
  }, [refresh]);

  if (loading) return <div className="empty">Loading…</div>;
  if (fatal) return <div className="signin"><div className="card"><div className="body"><div className="msg error">{fatal}</div></div></div></div>;

  if (session?.setup === "no-admin") {
    return (
      <div className="signin">
        <div className="card">
          <header><h1>The panel has no accounts yet</h1></header>
          <div className="body">
            <p>
              Before anyone can sign in, an Admin account has to be created from the Pages project's
              secrets — <span className="mono">SEED_ADMIN_USERNAME</span> and{" "}
              <span className="mono">SEED_ADMIN_PASSWORD</span>.
            </p>
            <p className="hint">The steps are in <span className="mono">admin/README.md</span>.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!session?.user) return <SignIn onSignedIn={refresh} />;

  const may = (action) => session.can.includes(action);

  return (
    <div className="shell">
      <aside className="side">
        <div className="mark">
          All Brains Clinic
          <small>website admin</small>
        </div>

        <nav>
          {NAV.filter((item) => !item.needs || may(item.needs)).map((item) => (
            <a
              key={item.path}
              href={`#/${item.path}`}
              aria-current={(route.parts[0] || "") === item.path ? "page" : undefined}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="who">
          <div>
            <strong>{session.user.displayName}</strong>
          </div>
          <div style={{ textTransform: "capitalize" }}>{session.user.role}</div>
          <div className="row" style={{ marginTop: "0.5rem" }}>
            <a className="btn" href="#/account" style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}>
              My account
            </a>
            <button
              className="quiet"
              style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}
              onClick={async () => {
                await api.post("auth/sign-out");
                refresh();
              }}
            >
              Sign out
            </button>
          </div>
          {session.credential === "token" && (
            <div className="msg warning" style={{ marginTop: "0.6rem", fontSize: "0.78rem" }}>
              Using a personal access token, not the GitHub App. Every change will be credited to
              whoever made the token.
            </div>
          )}
        </div>
      </aside>

      <main className="main">
        <Screen route={route} session={session} may={may} />
        <p className="footnote">
          This panel edits the public website only. Never put patient information into it — not in an
          article, not in a caption, not in a picture. Patient records live in the clinic's own
          systems (BC PIPA).
        </p>
      </main>
    </div>
  );
}

function Screen({ route, session, may }) {
  const [head, second] = route.parts;

  // Keyed by collection and by the address's filters, so moving from one list
  // to another — or from a dashboard card's filtered list to the nav's bare
  // one — starts the filters afresh rather than carrying them over.
  if (collectionBySlug(head)) {
    return second ? (
      <ItemForm key={`${head}/${second}`} collection={head} id={second} session={session} may={may} />
    ) : (
      <ItemList key={listKey(head, route.query)} collection={head} may={may} query={route.query} />
    );
  }

  switch (head) {
    case undefined:
    case "":
      return <Dashboard session={session} may={may} />;
    case "media":
      return <Media may={may} />;
    case "seo":
      return may("seo") ? <Seo may={may} /> : <NotAllowed />;
    case "home":
      return may("home") ? <HomePicks /> : <NotAllowed />;
    case "users":
      return may("users") ? <Users me={session.user} /> : <NotAllowed />;
    case "account":
      return <Account me={session.user} />;
    default:
      return (
        <div className="card">
          <div className="empty">
            There is no screen called “{head}”. <a href="#/">Back to the dashboard</a>.
          </div>
        </div>
      );
  }
}

const NotAllowed = () => (
  <div className="card">
    <div className="empty">
      That screen is for Publishers and Admins. <a href="#/">Back to the dashboard</a>.
    </div>
  </div>
);
